import { ApiError } from "../api/index.js";
import type { OmnaraApiShape } from "../api/index.js";
import type { ClientPlatform } from "../sloplog/originator.js";

/**
 * API client interface for RelayClient. Both OmnaraApiClient and
 * TracedOmnaraApiClient satisfy this via structural subtyping.
 */
type RelayCompatibleApiClient = OmnaraApiShape & {
  getAccessToken(): Promise<string | null>;
};
import {
  validateControlMessage,
  validateMessageEnvelope,
} from "../schemas/index.js";
import type { ControlMessage, MessageEnvelope } from "../schemas/index.js";
import { jitterDelayMs } from "../utils/jitter.js";

type RelayMessage = ControlMessage | MessageEnvelope;
type RelayListener = (message: RelayMessage) => void;
type ReadyStateListener = (readyState: number) => void;
type OpenEpochListener = (openEpoch: number) => void;
type AuthFailureListener = (authFailureEpoch: number) => void;

type RegistrationInfo = {
  url: string;
  heartbeatIntervalMs: number;
  expiresAtMs: number;
};

const MIN_HEARTBEAT_MS = 5_000;
const REGISTRATION_EXPIRY_SAFETY_MS = 30_000;
const MIN_RECONNECT_DELAY_MS = 250;
const MAX_RECONNECT_DELAY_MS = 30_000;

function backoffDelayMs(attempt: number): number {
  const cappedAttempt = Math.min(Math.max(attempt, 1), 10);
  const base = Math.min(
    1000 * Math.pow(1.5, cappedAttempt - 1),
    MAX_RECONNECT_DELAY_MS,
  );
  return jitterDelayMs(base, {
    jitterRatio: 0.25,
    minDelayMs: MIN_RECONNECT_DELAY_MS,
    maxDelayMs: MAX_RECONNECT_DELAY_MS,
    rounding: "floor",
  });
}

export class RelayClient {
  private readonly api: RelayCompatibleApiClient;
  private readonly userSessionId: string;
  private readonly clientKind: ClientPlatform;

  private socket: WebSocket | null = null;
  private readyState: number = WebSocket.CLOSED;
  private openEpoch = 0;
  private authFailureEpoch = 0;
  private authBlocked = false;

  private registration: RegistrationInfo | null = null;
  private registrationPromise: Promise<RegistrationInfo> | null = null;

  private readonly listeners = new Map<symbol, RelayListener>();
  private readonly readyStateListeners = new Map<symbol, ReadyStateListener>();
  private readonly openEpochListeners = new Map<symbol, OpenEpochListener>();
  private readonly authFailureListeners = new Map<
    symbol,
    AuthFailureListener
  >();

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private connectInFlight: Promise<void> | null = null;

  constructor(
    api: RelayCompatibleApiClient,
    userSessionId: string,
    clientKind: ClientPlatform,
  ) {
    this.api = api;
    this.userSessionId = userSessionId;
    this.clientKind = clientKind;
  }

  public getReadyState(): number {
    return this.readyState;
  }

  public getOpenEpoch(): number {
    return this.openEpoch;
  }

  public getAuthFailureEpoch(): number {
    return this.authFailureEpoch;
  }

  public subscribe(token: symbol, listener: RelayListener): () => void {
    this.listeners.set(token, listener);
    this.ensureConnected();
    return () => {
      this.listeners.delete(token);
      this.maybeClose();
    };
  }

  public subscribeReadyState(
    token: symbol,
    listener: ReadyStateListener,
  ): () => void {
    this.readyStateListeners.set(token, listener);
    listener(this.readyState);
    return () => {
      this.readyStateListeners.delete(token);
    };
  }

  public subscribeOpenEpoch(
    token: symbol,
    listener: OpenEpochListener,
  ): () => void {
    this.openEpochListeners.set(token, listener);
    listener(this.openEpoch);
    return () => {
      this.openEpochListeners.delete(token);
    };
  }

  public subscribeAuthFailure(
    token: symbol,
    listener: AuthFailureListener,
  ): () => void {
    this.authFailureListeners.set(token, listener);
    listener(this.authFailureEpoch);
    return () => {
      this.authFailureListeners.delete(token);
    };
  }

  public handleAuthChanged(): void {
    if (!this.authBlocked) {
      return;
    }
    this.authBlocked = false;
    this.registration = null;
    this.ensureConnected();
  }

  /**
   * Force a reconnection to the relay websocket.
   * This is useful when the session's worktree_type changes (e.g., during migration)
   * and we need to re-subscribe to the correct Redis channel.
   */
  public forceReconnect(): void {
    // Clear registration so we get fresh bootstrap with updated worktree_type
    this.registration = null;
    this.reconnectAttempt = 0;
    // Cleanup and reconnect
    this.cleanup("Force reconnect");
    this.notifyReadyState(WebSocket.CLOSED);
    this.ensureConnected();
  }

  public send(message: string): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      throw new Error("WebSocket not connected");
    }
    socket.send(message);
  }

  public sendJson(message: unknown): void {
    this.send(JSON.stringify(message));
  }

  private notifyReadyState(next: number): void {
    if (this.readyState === next) {
      return;
    }
    this.readyState = next;
    for (const handler of this.readyStateListeners.values()) {
      try {
        handler(next);
      } catch (error) {
        console.warn("[RelayClient] readyState handler threw", error);
      }
    }
  }

  private notifyOpenEpoch(): void {
    for (const handler of this.openEpochListeners.values()) {
      try {
        handler(this.openEpoch);
      } catch (error) {
        console.warn("[RelayClient] openEpoch handler threw", error);
      }
    }
  }

  private notifyAuthFailure(): void {
    for (const handler of this.authFailureListeners.values()) {
      try {
        handler(this.authFailureEpoch);
      } catch (error) {
        console.warn("[RelayClient] authFailure handler threw", error);
      }
    }
  }

  private isAuthFailure(error: unknown): boolean {
    if (error instanceof ApiError) {
      return error.status === 401 || error.status === 403;
    }
    if (error instanceof Error) {
      return error.message === "No access token available";
    }
    return false;
  }

  private markAuthFailure(): void {
    if (this.authBlocked) {
      return;
    }
    this.authBlocked = true;
    this.authFailureEpoch += 1;
    this.registration = null;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempt = 0;
    this.notifyAuthFailure();
  }

  private maybeClose(): void {
    if (this.listeners.size > 0) {
      return;
    }
    this.cleanup("No subscribers");
    this.notifyReadyState(WebSocket.CLOSED);
  }

  private ensureConnected(): void {
    if (this.listeners.size === 0) {
      return;
    }
    if (this.authBlocked) {
      return;
    }
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    void this.connect();
  }

  private async getRegistration(): Promise<RegistrationInfo> {
    const now = Date.now();
    if (this.registration && now < this.registration.expiresAtMs) {
      return this.registration;
    }

    if (this.registrationPromise) {
      return this.registrationPromise;
    }

    this.registrationPromise = (async () => {
      const accessToken = await this.api.getAccessToken();
      if (!accessToken) {
        throw new Error("No access token available");
      }

      const registration = await this.api.relay.registerRelay(
        {
          user_session_id: this.userSessionId,
          client_kind: this.clientKind,
        },
        accessToken,
      );

      const url = this.api.relay.buildRelayWebsocketUrl(
        registration.relay_ws_path,
        accessToken,
        registration.relay_token,
      );

      const heartbeatIntervalMs = Math.max(
        registration.relay_heartbeat_interval_sec * 1000,
        MIN_HEARTBEAT_MS,
      );

      const expiresAtMs =
        now +
        registration.relay_token_expires_in * 1000 -
        REGISTRATION_EXPIRY_SAFETY_MS;

      const info: RegistrationInfo = { url, heartbeatIntervalMs, expiresAtMs };
      this.registration = info;
      return info;
    })();

    try {
      return await this.registrationPromise;
    } finally {
      this.registrationPromise = null;
    }
  }

  private async connect(): Promise<void> {
    if (this.connectInFlight) {
      return this.connectInFlight;
    }

    this.connectInFlight = (async () => {
      if (this.listeners.size === 0) {
        return;
      }

      this.cleanup("Reconnect");
      this.notifyReadyState(WebSocket.CONNECTING);

      let registration: RegistrationInfo;
      try {
        registration = await this.getRegistration();
      } catch (error) {
        this.notifyReadyState(WebSocket.CLOSED);
        if (this.isAuthFailure(error)) {
          this.markAuthFailure();
          return;
        }
        this.scheduleReconnect();
        throw error;
      }

      if (this.listeners.size === 0) {
        this.notifyReadyState(WebSocket.CLOSED);
        return;
      }

      const socket = new WebSocket(registration.url);
      this.socket = socket;

      socket.onopen = () => {
        if (this.socket !== socket) {
          return;
        }

        // If we have no listeners by the time we open, close immediately.
        if (this.listeners.size === 0) {
          this.cleanup("Opened without subscribers");
          return;
        }

        this.reconnectAttempt = 0;
        this.openEpoch += 1;
        this.notifyOpenEpoch();
        this.notifyReadyState(WebSocket.OPEN);
        this.startHeartbeat(registration.heartbeatIntervalMs);
      };

      socket.onmessage = (event: MessageEvent<string>) => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(String(event.data));
        } catch {
          return;
        }

        let message: RelayMessage | null = null;
        if (!(parsed && typeof parsed === "object" && "frame_kind" in parsed)) {
          return;
        }
        const framed = parsed as { frame_kind?: unknown };
        if (framed.frame_kind === "message_envelope") {
          if (validateMessageEnvelope(parsed)) {
            message = parsed;
          } else {
            return;
          }
        } else if (framed.frame_kind === "control") {
          if (validateControlMessage(parsed)) {
            message = parsed;
          } else {
            return;
          }
        } else {
          return;
        }

        for (const handler of this.listeners.values()) {
          try {
            handler(message);
          } catch (error) {
            console.warn("[RelayClient] subscriber threw", error);
          }
        }
      };

      socket.onerror = () => {
        // Let onclose drive reconnection; some platforms do not provide rich error details.
      };

      socket.onclose = (event: unknown) => {
        if (this.socket !== socket) {
          return;
        }
        const closeEvent = event as
          | { code?: number; reason?: string; wasClean?: boolean }
          | undefined;
        if (closeEvent?.code === 4401) {
          this.registration = null;
        }
        this.cleanup("Socket closed");
        this.notifyReadyState(WebSocket.CLOSED);
        this.scheduleReconnect();
      };
    })();

    try {
      await this.connectInFlight;
    } finally {
      this.connectInFlight = null;
    }
  }

  private startHeartbeat(intervalMs: number): void {
    if (this.heartbeatTimer) {
      return;
    }

    const interval = Math.max(intervalMs, MIN_HEARTBEAT_MS);
    this.heartbeatTimer = setInterval(() => {
      const socket = this.socket;
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        return;
      }
      socket.send(
        JSON.stringify({
          frame_kind: "control",
          type: "heartbeat",
          mode: "ping",
          sent_at: new Date().toISOString(),
        }),
      );
    }, interval);
  }

  private scheduleReconnect(): void {
    if (this.listeners.size === 0) {
      return;
    }
    if (this.reconnectTimer) {
      return;
    }
    if (this.authBlocked) {
      return;
    }

    const delay = backoffDelayMs((this.reconnectAttempt += 1));
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.ensureConnected();
    }, delay);
  }

  private cleanup(reason: string): void {
    void reason;
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      if (
        socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING
      ) {
        try {
          socket.close(1000, reason);
        } catch {
          // ignore close failures
        }
      }
    }
  }
}

type RelayClientKey = string;
type RelayClientEntry = {
  client: RelayClient;
};

const relayClientsByKey = new Map<RelayClientKey, RelayClientEntry>();

export function getRelayClient(
  api: RelayCompatibleApiClient,
  userSessionId: string,
  clientKind: ClientPlatform,
): RelayClient {
  const key = `${userSessionId}:${clientKind}`;
  const existing = relayClientsByKey.get(key);
  if (existing) {
    return existing.client;
  }
  const client = new RelayClient(api, userSessionId, clientKind);
  relayClientsByKey.set(key, { client });
  return client;
}

export function forceRelayReconnectForSession(
  userSessionId: string,
  clientKind: ClientPlatform,
): void {
  const existing = relayClientsByKey.get(`${userSessionId}:${clientKind}`);
  existing?.client.forceReconnect();
}

export function notifyRelayAuthChanged(): void {
  for (const entry of relayClientsByKey.values()) {
    entry.client.handleAuthChanged();
  }
}
